import { CartAddPendingEvent, ThemeEvents } from '@theme/events';

let pendingCount = 0;
/** @type {string[]} */
const pendingOptimisticIds = [];

/**
 * @param {string} [preferredId]
 * @returns {string}
 */
function consumeOptimisticId(preferredId = '') {
  if (preferredId) {
    const index = pendingOptimisticIds.indexOf(preferredId);
    if (index !== -1) {
      pendingOptimisticIds.splice(index, 1);
      return preferredId;
    }
  }
  return pendingOptimisticIds.shift() || '';
}

// ─── Checkout button loading state ───────────────────────────────────────────

/**
 * @param {boolean} adding
 */
function setAddingState(adding) {
  const drawer = document.querySelector('cart-drawer-component');
  drawer?.toggleAttribute('cart-adding', adding);

  const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById('checkout'));
  if (btn) {
    btn.classList.toggle('is-loading', adding);
    btn.setAttribute('aria-busy', adding ? 'true' : 'false');
    btn.disabled = adding;
  }
}

// ─── Optimistic cart item shell ───────────────────────────────────────────────

/** Escape a string for safe insertion into HTML attribute values or text nodes. */
/**
 * @param {string} str
 * @returns {string}
 */
function esc(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** @returns {Record<string, string>} */
function getThemeTranslations() {
  return /** @type {Record<string, string>} */ (window.Theme?.translations || {});
}

/**
 * Build the unlocked free-delivery message for optimistic cart UI.
 * @returns {string}
 */
function getShippingProgressSuccessText() {
  const translations = getThemeTranslations();
  const goal = translations.shipping_progress_goal || '';
  const template = translations.shipping_progress_success || '';
  if (!template) return goal;
  if (template.includes('[goal]')) {
    return template.replace('[goal]', goal);
  }
  return goal ? `${template} ${goal}`.trim() : template;
}

/**
 * Parse money-like text into a number for compare-price checks.
 * Handles common formats like "83,95", "1,234.56", "1.234,56".
 * @param {string} value
 * @returns {number | null}
 */
function parseMoneyNumber(value) {
  const raw = (value || '').trim().replace(/[^\d.,-]/g, '');
  if (!raw) return null;

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');
  let normalized = raw;

  if (hasComma && hasDot) {
    if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      normalized = raw.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = raw.replace(/,/g, '');
    }
  } else if (hasComma) {
    normalized = raw.replace(',', '.');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Collapse whitespace so money values stay on one line.
 * @param {string} value
 * @returns {string}
 */
function normalizeInlineText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

/**
 * Builds one <tr> shell that mirrors the real cart-items__table-row structure.
 * The row carries `data-optimistic` so it can be found and removed if needed,
 * though in practice morphSection will replace it before anyone notices.
 *
 * @param {{ imageUrl: string, productTitle: string, price: string, productUrl: string, optimisticId?: string, qty?: number, variantSummary?: string, variantId?: string, comparePrice?: string, priceEach?: string, hasNro?: boolean, isNroSelected?: boolean, subscriptionDiscountPercent?: string, subscriptionReceive?: string }} data
 */
function buildOptimisticRow({
  imageUrl,
  productTitle,
  price,
  productUrl,
  optimisticId = '',
  qty = 1,
  variantSummary = '',
  variantId = '',
  comparePrice = '',
  priceEach = '',
  hasNro = false,
  isNroSelected = false,
  subscriptionDiscountPercent = '',
  subscriptionReceive = '',
}) {
  const translations = getThemeTranslations();
  const discountedLabel = esc(translations.cart_pending_discounted || '');
  const neverRunOutLabel = esc(translations.cart_pending_never_run_out || '');
  const neverRunOutSubtitle = esc(translations.cart_pending_never_run_out_subtitle || '');
  const loadingLabel = esc(translations.cart_pending_loading || '');
  const decreaseQuantityLabel = esc(translations.cart_pending_decrease_quantity || '');
  const increaseQuantityLabel = esc(translations.cart_pending_increase_quantity || '');
  const quantityLabel = esc(translations.cart_pending_quantity || '');
  const removeLabel = esc(translations.cart_pending_remove || '');
  const href = esc(productUrl || '#');
  const title = esc((productTitle || '').trim() || ' ');
  const summary = esc((variantSummary || '').trim());
  const displayPrice = esc(normalizeInlineText(price || '') || ' ');
  const rawComparePrice = normalizeInlineText(comparePrice || '');
  const priceAmount = parseMoneyNumber(displayPrice);
  const compareAmount = parseMoneyNumber(rawComparePrice);
  const shouldShowCompare = isNroSelected
    ? /\d/.test(rawComparePrice)
    : !!rawComparePrice && compareAmount != null && (priceAmount == null || compareAmount > priceAmount);
  const displayComparePrice = shouldShowCompare ? esc(rawComparePrice) : '';
  const displayPriceEach = esc(normalizeInlineText(priceEach || ''));
  const displaySubscriptionDiscountPercent = esc((subscriptionDiscountPercent || '').trim());
  const displaySubscriptionReceive = esc((subscriptionReceive || '').trim());
  const displayQty = Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 1;
  const imgHtml = imageUrl
    ? `<a href="${href}" class="cart-items__media-container" style="--ratio:1;" tabindex="-1">
         <img src="${esc(imageUrl)}" class="cart-items__media-image border-style" alt="${title}" loading="eager">
       </a>`
    : `<div class="cart-items__media-container" style="--ratio:1;"></div>`;

  return `
    <tr role="row" class="cart-items__table-row" data-optimistic="true" data-optimistic-id="${esc(optimisticId)}">
      <td class="cart-items__media" role="cell" headers="productImage">
        ${imgHtml}
      </td>
      <td class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
        <p><a href="${href}" class="cart-items__title">${title}</a></p>
        ${summary
      ? `<dl class="cart-items__variants">
          <div class="cart-items__variant"><dd>${summary}</dd></div>
        </dl>`
      : ''}
        ${isNroSelected && displaySubscriptionReceive
      ? `<div class="cart-items__subscription">
          <span class="cart-items__subscription-receive-wrap">
            <span class="cart-items__subscription-label cart-items__subscription-icon">
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="icon-arrows-clock-wise" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M13.766 7.79h3.75V4.04"></path>
                  <path d="M5.14 5.14a6.867 6.867 0 0 1 9.72 0l2.656 2.65M6.234 12.21h-3.75v3.75"></path>
                  <path d="M14.86 14.86a6.869 6.869 0 0 1-9.72 0l-2.656-2.65"></path>
                </g>
              </svg>
            </span>
            <span class="cart-items__subscription-receive">${displaySubscriptionReceive}</span>
          </span>
        </div>`
      : ''}
      </td>
      <td class="cart-items__price" role="cell" headers="productTotal">
        <div class="cart-items__price-content">
          <div class="cart-items__price-row${isNroSelected ? ' cart-items__price-row--subscription' : ''}">
            ${displayComparePrice ? `<span class="cart-items__price-compare compare-at-price">${displayComparePrice}</span>` : ''}
            <span class="cart-items__price-total${displayComparePrice ? ' cart-items__price-total--discounted' : ''}">${displayPrice}</span>
          </div>
          ${displayPriceEach ? `<span class="cart-items__price-each cart-secondary-typography">${displayPriceEach}</span>` : ''}
        </div>
      </td>
      <td class="cart-items__quantity" role="cell" headers="quantity">
        <div class="cart-items__quantity-controls${isNroSelected ? ' cart-items__quantity-controls--subscription' : ''}" aria-disabled="true">
          ${isNroSelected
      ? `${displaySubscriptionDiscountPercent
        ? `<span class="cart-items__subscription-discount">
                <span class="cart-items__subscription-discount-percent">${displaySubscriptionDiscountPercent}</span>
                <span class="cart-items__subscription-discount-text">${discountedLabel}</span>
                <span class="cart-items__subscription-discount-icon"><svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path id="icon-check" d="m10.125 3.375-5.25 5.25L2.25 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg></span>
              </span>`
        : ''}`
      : `<div class="quantity-selector-wrapper">
            <cart-quantity-selector-component class="quantity-selector cart-primary-typography" data-variant-id="${esc(variantId)}">
              <button class="button quantity-minus button-unstyled" type="button" name="minus" ref="minusButton" disabled>
                <span class="visually-hidden">${decreaseQuantityLabel}</span><span class="svg-wrapper icon-plus">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                  </svg>
                </span>
              </button>
              <input type="number" value="${displayQty}" min="1" step="1" readonly aria-label="${quantityLabel}" ref="quantityInput" data-cart-quantity="${displayQty}" data-min="1">
              <button class="button quantity-plus button-unstyled" type="button" name="plus" ref="plusButton" disabled>
                <span class="visually-hidden">${increaseQuantityLabel}</span><span class="svg-wrapper icon-plus">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                    <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                  </svg>
                </span>
              </button>
            </cart-quantity-selector-component>
          </div>`}
          <button class="button button--tertiary cart-items__remove" type="button" disabled>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <span class="visually-hidden">${removeLabel}</span>
          </button>
        </div>
      </td>
      <td class="cart-items__error hidden" role="cell" headers="quantity">
        <div class="cart-item__error" role="alert">
          <span class="svg-wrapper">
            <svg class="icon icon-error" viewBox="0 0 13 13">
              <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"></circle>
              <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle>
              <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
            </svg>
          </span>
          <small class="cart-item__error-text cart-primary-typography"></small>
        </div>
      </td>
      ${hasNro && !isNroSelected
      ? `<td class="cart-items__nro-cell" role="cell" headers="productInformation">
        <div class="cart-items__nro" data-optimistic-nro="true">
          <button type="button" class="cart-items__nro-toggle button-unstyled" aria-expanded="false" disabled>
            <span class="cart-items__nro-toggle-main">
              <span class="cart-items__nro-toggle-checkbox" aria-hidden="true"></span>
              <span class="cart-items__nro-toggle-copy">
                <span class="cart-items__nro-toggle-text">${neverRunOutLabel}</span>
                <span class="cart-items__nro-toggle-subtitle">${neverRunOutSubtitle}</span>
              </span>
            </span>
            <span class="cart-items__nro-toggle-discount">${loadingLabel}</span>
          </button>
        </div>
      </td>`
      : ''}
    </tr>`;
}

function ensureDrawerHasSummary() {
  const drawerDialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
  if (drawerDialog instanceof HTMLElement) {
    drawerDialog.classList.remove('cart-drawer--empty');
  }

  const emptyHeading = document.querySelector('.cart-drawer__heading--empty');
  if (emptyHeading instanceof HTMLElement) {
    emptyHeading.remove();
  }

  const content = document.querySelector('cart-drawer-component .cart-drawer__content');
  if (!(content instanceof HTMLElement)) return;
  if (content.querySelector('.cart-drawer__summary')) return;

  const translations = getThemeTranslations();
  const estimatedTotal = esc(translations.cart_estimated_total || '');
  const taxNote = esc(translations.taxes_included_shipping_at_checkout || '');
  const checkoutLabel = esc(translations.checkout || '');

  content.insertAdjacentHTML(
    'beforeend',
    `<div class="cart-drawer__summary" data-optimistic-summary="true">
      <div class="cart__summary-totals">
        <div class="cart__total-container">
          <span class="cart__summary-item cart__total" role="status">
            <span class="cart__total-label cart-primary-typography">${estimatedTotal}</span>
            <text-component class="cart__total-value cart-secondary-typography">…</text-component>
          </span>
          <div class="cart__summary-item tax-note cart-primary-typography">
            ${taxNote}
          </div>
        </div>
      </div>
      <div class="cart__ctas">
        <button
          type="submit"
          id="checkout"
          class="cart__checkout-button button"
          name="checkout"
          form="cart-form"
        >
          <span class="cart__checkout-text">${checkoutLabel}</span>
          <span class="cart__checkout-spinner" aria-hidden="true"></span>
        </button>
      </div>
    </div>`
  );
}

function ensureDrawerHeaderPlaceholder() {
  const header = document.querySelector('cart-drawer-component .cart-drawer__header');
  if (!(header instanceof HTMLElement)) return;

  if (!header.querySelector('.cart-drawer__heading')) {
    const translations = getThemeTranslations();
    const cartTitle = esc(translations.cart_title || '');
    const shippingCopy = esc(getShippingProgressSuccessText());
    const shippingAriaLabel = esc(translations.shipping_progress_aria_label || translations.shipping_progress_goal || '');

    header.insertAdjacentHTML(
      'afterbegin',
      `<div class="cart-drawer__header-main">
        <h2 class="cart-drawer__heading" id="cart-drawer-heading">${cartTitle}</h2>
        <div class="cart-drawer__shipping-progress">
          <p class="cart-drawer__shipping-progress-text">
            <span class="cart-drawer__shipping-progress-copy">${shippingCopy}</span>
          </p>
          <div
            class="cart-drawer__shipping-progress-track"
            role="progressbar"
            aria-label="${shippingAriaLabel}"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="0"
          >
            <span class="cart-drawer__shipping-progress-fill" style="width: 0%;"></span>
          </div>
        </div>
      </div>`
    );
  }
}

function clearEmptyDrawerUI() {
  const emptyHeading = document.querySelector('.cart-drawer__heading--empty');
  if (emptyHeading instanceof HTMLElement) {
    emptyHeading.remove();
  }

  const emptyButton = document.querySelector('.cart-items__empty-button');
  if (emptyButton instanceof HTMLElement) {
    emptyButton.remove();
  }

  const drawerDialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
  if (drawerDialog instanceof HTMLElement) {
    drawerDialog.classList.remove('cart-drawer--empty');
  }

  ensureDrawerHeaderPlaceholder();
}

/**
 * @param {string} optimisticId
 */
function rollbackOptimisticItem(optimisticId) {
  let row = null;
  if (optimisticId) {
    row = Array.from(document.querySelectorAll('.cart-items__table-row[data-optimistic-id]')).find(
      (el) => el instanceof HTMLElement && el.getAttribute('data-optimistic-id') === optimisticId
    );
  }
  if (!(row instanceof HTMLElement)) {
    row = document.querySelector('.cart-items__table-row[data-optimistic="true"]');
  }
  if (row instanceof HTMLElement) {
    row.remove();
  }
}

/**
 * Inject a shell cart row so the drawer shows something real immediately,
 * before the Shopify cart API responds. morphSection will replace all of
 * this with accurate server-rendered content once the fetch completes.
 *
 * @param {{ optimisticId?: string, imageUrl?: string, productTitle?: string, price?: string, qty?: number, productUrl?: string, variantSummary?: string, variantId?: string, comparePrice?: string, priceEach?: string, hasNro?: boolean, isNroSelected?: boolean, subscriptionDiscountPercent?: string, subscriptionReceive?: string }} detail
 */
function injectOptimisticItem(detail) {
  const wrapper = document.querySelector('.cart-items__wrapper');
  if (!wrapper) return;

  const row = buildOptimisticRow({
    optimisticId: detail.optimisticId || '',
    imageUrl: detail.imageUrl || '',
    productTitle: detail.productTitle || '',
    price: detail.price || '',
    productUrl: detail.productUrl || '',
    qty: detail.qty || 1,
    variantSummary: detail.variantSummary || '',
    variantId: detail.variantId || '',
    comparePrice: detail.comparePrice || '',
    priceEach: detail.priceEach || '',
    hasNro: detail.hasNro === true,
    isNroSelected: detail.isNroSelected === true,
    subscriptionDiscountPercent: detail.subscriptionDiscountPercent || '',
    subscriptionReceive: detail.subscriptionReceive || '',
  });

  const existingTbody = wrapper.querySelector('.cart-items__table tbody');

  if (existingTbody) {
    // Cart already has items — append optimistic row to the existing table.
    existingTbody.insertAdjacentHTML('beforeend', row);
  } else {
    // Cart is empty — replace the empty state with a minimal table so the
    // drawer shows the item instead of the "Close Cart" button.
    wrapper.innerHTML = `
      <form action="/cart" class="cart-form" id="cart-form" method="post">
        <div class="cart-items spacing-style">
          <table class="cart-items__table" role="table">
            <thead class="visually-hidden" role="rowgroup">
              <tr role="row" class="cart-items__table-row">
                <th id="productImage" scope="col" role="columnheader"></th>
                <th id="productInformation" scope="col" role="columnheader"></th>
                <th id="quantity" scope="col" role="columnheader"></th>
                <th id="productTotal" scope="col" role="columnheader"></th>
              </tr>
            </thead>
            <tbody role="rowgroup">${row}</tbody>
          </table>
        </div>
      </form>`;
  }

  ensureDrawerHasSummary();
}

// ─── Event listeners ──────────────────────────────────────────────────────────

document.addEventListener(
  CartAddPendingEvent.eventName,
  (event) => {
    const e = /** @type {CartAddPendingEvent} */ (event);
    pendingCount++;
    pendingOptimisticIds.push(e.detail?.optimisticId || '');
    clearEmptyDrawerUI();
    setAddingState(true);
    injectOptimisticItem(e.detail || {});
  },
  { capture: true }
);

// CartAddEvent (success or error) fires with source 'product-form-component' — resolve each pending add.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail?.data;
  if (detail?.source !== 'product-form-component') return;

  const optimisticId = consumeOptimisticId(detail.optimisticId || '');
  if (detail.didError) {
    rollbackOptimisticItem(optimisticId);
  }

  pendingCount = Math.max(0, pendingCount - 1);
  if (pendingCount === 0) setAddingState(false);
});

document.addEventListener(ThemeEvents.cartError, () => {
  if (pendingCount <= 0) return;
  rollbackOptimisticItem(pendingOptimisticIds[0] || '');
});
